import BaseApi from '@/api/Base/BaseApi'
import {
  CreateDataDriftNodePayload,
  DataDriftNodeInfo,
  DataDriftNodeItem,
  ExportDqiItemsPayload,
  GetDataDriftNodeItemCountsResponse,
  GetDataDriftNodeItemsPayload,
  GetDataDriftNodeItemsResponse,
  GetDqiChartDataPayload,
  GetDqiChartDataResponse,
  GetMedianChartDataPayload,
  GetMedianChartDataResponse,
  MedianSummary,
  UpdateDataDriftNodePayload,
  UpdateDataDriftSettingsPayload,
  UpdateDriftNotificationSettingsPayload
} from '@/api/DataDrift/types'
import { omitNilOrEmptyStringFromObject } from '@/utils/common'

const ROOT = '/data-drift'
const defaultPaginationOptions = {
  itemsPerPage: 10,
  page: 0,
  sortDir: 'desc',
  sortKey: 'createdAt',
}

export default class DataDriftNodeApi extends BaseApi {
  createDataDriftNode(payload: CreateDataDriftNodePayload) {
    const { projectId, ...rest } = payload
    return this.agent.post<DataDriftNodeInfo>(`/project/${projectId}/data-drift/create`, rest)
  }

  getDataDriftNodeDetail(dataDriftNodeId: string) {
    return this.agent.get<DataDriftNodeInfo>(`${ROOT}/${dataDriftNodeId}`)
  }

  updateDataDriftNode(payload: UpdateDataDriftNodePayload) {
    const { dataDriftNodeId, ...rest } = payload
    return this.agent.put(`${ROOT}/${dataDriftNodeId}`, rest)
  }

  getDataDriftNodeItems(payload: GetDataDriftNodeItemsPayload) {
    const { dataDriftNodeId, page, itemsPerPage, sortKey, sortDir, keyword, ...rest } = payload ?? defaultPaginationOptions
    const params = omitNilOrEmptyStringFromObject({
      page,
      num_per_page: itemsPerPage,
      sort_key: sortKey,
      sort_dir: sortDir,
      search: keyword,
      ...rest,
    })
    return this.agent.get<GetDataDriftNodeItemsResponse>(`${ROOT}/${dataDriftNodeId}/list`, { params })
  }

  getDataDriftNodeItemCounts(payload: GetDataDriftNodeItemsPayload) {
    const { dataDriftNodeId, from, to } = payload
    return this.agent.get<GetDataDriftNodeItemCountsResponse>(`${ROOT}/${dataDriftNodeId}/item-count`, {
      params: {
        from,
        to,
      },
    })
  }

  getDqiChartData(payload: GetDqiChartDataPayload) {
    const { dataDriftNodeId, ...rest } = payload
    return this.agent.get<GetDqiChartDataResponse>(`${ROOT}/${dataDriftNodeId}/dqi`, {
      params: rest,
    })
  }

  toggleExcludeItem(dataDriftItemId: string) {
    return this.agent.post<DataDriftNodeItem>(`/data-drift-item/${dataDriftItemId}/exclude`)
  }

  runDqi(dataDriftNodeId: string) {
    return this.agent.post(`${ROOT}/${dataDriftNodeId}/dqi`)
  }

  exportDqiItems(payload: ExportDqiItemsPayload) {
    const { dataDriftNodeId, ...rest } = payload
    const data = omitNilOrEmptyStringFromObject(rest)
    return this.agent.post(`${ROOT}/${dataDriftNodeId}/export-items`, data)
  }

  // getMedianChartData(payload: GetMedianChartDataPayload) {
  //   const { dataDriftNodeId, ...rest } = payload
  //   return this.agent.get<GetMedianChartDataResponse>(`${ROOT}/${dataDriftNodeId}/median`, {
  //     params: rest,
  //   })
  // }

  updateNotificationSettings(payload: UpdateDriftNotificationSettingsPayload) {
    const { dataDriftNodeId, ...rest } = payload
    return this.agent.put<DataDriftNodeInfo>(`${ROOT}/${dataDriftNodeId}/notification-settings`, rest)
  }

  updateDataDriftSettings(payload: UpdateDataDriftSettingsPayload) {
    const { dataDriftNodeId, ...rest } = payload
    return this.agent.put<DataDriftNodeInfo>(`${ROOT}/${dataDriftNodeId}/settings`, rest)
  }

  getMedianSummaries(dataDriftNodeId: string, unit: string, from: string, to: string) {
    return this.agent.get<MedianSummary[]>(`${ROOT}/${dataDriftNodeId}/unit-median`, {
      params: {
        unit,
        from,
        to,
      },
    })
  }
}
